import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import type { DocumentResponse } from '@timesheet/schemas';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { documentsService } from '../../services/documentsService';
import { useNotification } from '../../contexts/NotificationContext';

interface ConfirmDeleteDocumentDialogProps {
  doc: DocumentResponse | null;
  open: boolean;
  onClose: () => void;
  onDeleted: (doc: DocumentResponse) => void;
}

export const ConfirmDeleteDocumentDialog: React.FC<ConfirmDeleteDocumentDialogProps> = ({ doc, open, onClose, onDeleted }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [deleting, setDeleting] = useState(false);

  const confirm = async () => {
    if (!doc) return;
    try {
      setDeleting(true);
      await documentsService.remove(doc.id);
      showNotification(t('documents.delete.success', 'Documento eliminato'), 'success');
      onDeleted(doc);
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && !deleting && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('documents.delete.title', 'Eliminare il documento?')}</DialogTitle>
          <DialogDescription>
            {t('documents.delete.description', 'Il file non sarà più visibile a nessuno. Questa azione non si può annullare.')}
          </DialogDescription>
        </DialogHeader>
        {doc && (
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <p className="font-medium">{doc.title}</p>
            <p className="text-muted-foreground">{doc.originalFileName}</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>
            {t('common.cancel', 'Annulla')}
          </Button>
          <Button variant="destructive" onClick={confirm} disabled={deleting}>
            {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {deleting ? t('common.deleting', 'Eliminazione...') : t('documents.delete.confirm', 'Elimina')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
